import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Swords, X, Plus, Trophy, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { playing: "Đang đấu", finished: "Đã kết thúc", cancelled: "Hủy" };

export default function AdminMatches() {
  const nav = useNavigate();
  const loc = useLocation();
  const isNew = loc.pathname.endsWith("/new");
  const [matches, setMatches] = useState([]);
  const [tab, setTab] = useState("playing");
  const [finishing, setFinishing] = useState(null);

  const load = () => api.get(`/matches?status=${tab}&limit=100`).then(({ data }) => setMatches(data));
  useEffect(() => { load(); }, [tab]);

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Trận đấu</h1>
          <p className="text-white/40 text-sm mt-1">Tạo, theo dõi và nhập kết quả</p>
        </div>
        <button onClick={() => nav("/admin/matches/new")} data-testid="new-match-btn" className="px-4 py-2.5 rounded-md text-sm font-medium bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center gap-2">
          <Plus size={15} /> Tạo trận
        </button>
      </div>

      <div className="flex gap-2 mb-5">
        {["playing", "finished", "cancelled"].map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`match-tab-${s}`}
            className={`px-4 py-2 rounded-md text-sm ${tab === s ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/30" : "bg-white/[0.03] text-white/60 border border-white/8"}`}>
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="rounded-xl bg-[#0c0d14] border border-white/8 overflow-hidden">
        {matches.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">Không có trận</div>
        ) : matches.map((m) => (
          <div key={m.id} className="border-b border-white/5 p-4 hover:bg-white/[0.03]" data-testid="match-row">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 w-20">Bàn {m.table_number}</div>
                <div className="flex-1 grid grid-cols-3 items-center gap-2 min-w-0">
                  <div className={`text-right truncate ${m.winner_id === m.player_a_id ? "text-cyan-300 font-semibold" : ""}`}>
                    <div className="text-sm truncate">{m.player_a_nickname}</div>
                    <div className="text-[10px] text-white/40">{m.player_a_rank}</div>
                  </div>
                  <div className="text-center">
                    {m.status === "finished" ? (
                      <div className="font-display font-bold text-xl">{m.score_a}-{m.score_b}</div>
                    ) : (
                      <div className="font-display font-bold text-xl text-white/40">VS</div>
                    )}
                    <div className="text-[10px] text-white/40">BO{m.best_of} · {m.mode}</div>
                  </div>
                  <div className={`truncate ${m.winner_id === m.player_b_id ? "text-cyan-300 font-semibold" : ""}`}>
                    <div className="text-sm truncate">{m.player_b_nickname}</div>
                    <div className="text-[10px] text-white/40">{m.player_b_rank}</div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {m.status === "playing" && (
                  <>
                    <button onClick={() => setFinishing(m)} data-testid={`finish-${m.id}`} className="px-3 py-1.5 rounded-md text-xs bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20">Nhập kết quả</button>
                    <button onClick={async () => { await api.post(`/matches/${m.id}/cancel`); load(); }} className="px-3 py-1.5 rounded-md text-xs text-white/60 hover:bg-white/5">Hủy</button>
                  </>
                )}
                {m.status === "finished" && (
                  <div className="text-xs text-cyan-300 font-mono-num">
                    +{m.winner_id === m.player_a_id ? m.elo_delta_a : m.elo_delta_b} ELO
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isNew && <NewMatchModal onClose={() => nav("/admin/matches")} onDone={() => { nav("/admin/matches"); load(); }} />}
      {finishing && <FinishModal m={finishing} onClose={() => setFinishing(null)} onDone={() => { setFinishing(null); load(); }} />}
    </div>
  );
}

const Modal = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
    <div className={`w-full ${wide ? "max-w-3xl" : "max-w-md"} rounded-xl bg-[#0c0d14] border border-white/10 p-6 max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-white/5 rounded"><X size={16} /></button>
      </div>
      {children}
    </div>
  </div>
);

const NewMatchModal = ({ onClose, onDone }) => {
  const [players, setPlayers] = useState([]);
  const [tables, setTables] = useState([]);
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [table, setTable] = useState(null);
  const [bo, setBo] = useState(1);
  const [mode, setMode] = useState("rank");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/players?limit=500").then(({ data }) => setPlayers(data));
    api.get("/tables").then(({ data }) => setTables(data));
  }, []);

  const submit = async () => {
    if (!a || !b || !table) return toast.error("Chọn 2 người chơi & 1 bàn");
    if (a === b) return toast.error("2 người chơi phải khác nhau");
    try {
      await api.post("/matches", { player_a_id: a, player_b_id: b, table_id: table, best_of: Number(bo), mode });
      toast.success("Đã tạo trận");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  const filtered = players.filter((p) => p.nickname.toLowerCase().includes(q.toLowerCase()));

  const PlayerPicker = ({ value, onChange, exclude, testid }) => (
    <div className="rounded-md bg-white/[0.03] border border-white/10 p-2 max-h-56 overflow-y-auto" data-testid={testid}>
      {filtered.filter((p) => p.id !== exclude).map((p) => (
        <button key={p.id} onClick={() => onChange(p.id)}
          className={`w-full flex items-center gap-2 p-1.5 rounded text-left ${value === p.id ? "bg-cyan-400/15 text-cyan-300" : "hover:bg-white/5"}`}>
          <img src={p.avatar_url} alt="" className="w-6 h-6 rounded-full" />
          <span className="text-xs flex-1 truncate">{p.nickname}</span>
          <span className="text-[10px] text-white/40">{p.elo}</span>
        </button>
      ))}
    </div>
  );

  return (
    <Modal title="Tạo trận mới" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm người chơi..." className="w-full bg-white/[0.03] border border-white/10 rounded pl-9 pr-3 py-2 text-sm outline-none focus:border-cyan-400/60" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Người chơi A</div>
            <PlayerPicker value={a} onChange={setA} exclude={b} testid="picker-a" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Người chơi B</div>
            <PlayerPicker value={b} onChange={setB} exclude={a} testid="picker-b" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Bàn</div>
            <select value={table || ""} onChange={(e) => setTable(e.target.value)} data-testid="select-table" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="">-- Chọn bàn --</option>
              {tables.map((t) => (
                <option key={t.id} value={t.id} disabled={t.status === "maintenance" || t.status === "playing"}>
                  Bàn {t.number} {t.status !== "free" ? `(${t.status})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Best Of</div>
            <select value={bo} onChange={(e) => setBo(e.target.value)} data-testid="select-bo" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value={1}>BO1</option>
              <option value={3}>BO3</option>
              <option value={5}>BO5</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1.5">Chế độ</div>
            <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="select-mode" className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm">
              <option value="rank">Rank</option>
              <option value="friendly">Giao hữu</option>
            </select>
          </div>
        </div>

        <button onClick={submit} data-testid="submit-match" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center justify-center gap-2">
          <Swords size={15} /> Bắt đầu trận
        </button>
      </div>
    </Modal>
  );
};

const FinishModal = ({ m, onClose, onDone }) => {
  const target = Math.floor(m.best_of / 2) + 1;
  const [sa, setSa] = useState(target);
  const [sb, setSb] = useState(0);
  const [winner, setWinner] = useState("a");

  const setWin = (side) => {
    setWinner(side);
    if (side === "a") { setSa(target); if (sb >= target) setSb(target - 1); }
    else { setSb(target); if (sa >= target) setSa(target - 1); }
  };

  const submit = async () => {
    if (sa === sb || Math.max(sa, sb) < target) return toast.error(`Tỷ số không hợp lệ cho BO${m.best_of}`);
    try {
      await api.post(`/matches/${m.id}/finish`, { score_a: Number(sa), score_b: Number(sb) });
      toast.success("Đã lưu kết quả");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  return (
    <Modal title="Nhập kết quả" onClose={onClose}>
      <div className="text-xs text-white/50 mb-4">BO{m.best_of} · Người thắng cần đạt {target} ván</div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {["a", "b"].map((side) => {
          const nick = side === "a" ? m.player_a_nickname : m.player_b_nickname;
          const val = side === "a" ? sa : sb;
          const setter = side === "a" ? setSa : setSb;
          return (
            <button key={side} onClick={() => setWin(side)} data-testid={`pick-winner-${side}`}
              className={`p-4 rounded-md border ${winner === side ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 bg-white/[0.03]"}`}>
              <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Người chơi {side.toUpperCase()}</div>
              <div className="font-display font-semibold text-sm truncate">{nick}</div>
              <input type="number" min="0" value={val} onChange={(e) => setter(Number(e.target.value))} data-testid={`score-${side}`}
                className="mt-2 w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-center font-display font-bold text-2xl" />
            </button>
          );
        })}
      </div>
      <button onClick={submit} data-testid="submit-finish" className="w-full py-2.5 rounded-md font-display font-semibold text-sm bg-gradient-to-r from-cyan-400 to-purple-500 text-black flex items-center justify-center gap-2">
        <Trophy size={15} /> Xác nhận kết quả
      </button>
    </Modal>
  );
};
